export const LIVE_PLAN_STATUSES = ["trialing", "active", "past_due"];

export const isPerLocationPlan = (plan) =>
  plan?.billing_type === "per_month" || plan?.unit === "location";

export const isStandalonePlan = (plan) => isPerLocationPlan(plan);

export const isMainPlan = (plan) => !isStandalonePlan(plan);

const billingListingKey = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[&+]/g, "and");
  return normalized.replace(/[^a-z0-9]+/g, "");
};

export const isHiddenFromBillingListing = (plan) => {
  const hidden = [
    "careplanning",
    "careculture",
    "profilereviews",
    "profileandreviews",
  ];
  const code = billingListingKey(plan?.code);
  const name = billingListingKey(plan?.name);
  return hidden.includes(code) || hidden.includes(name);
};

export const isBundlePlan = (plan) => {
  if (!plan) return false;
  if (plan.is_bundle || plan.is_all_modules) return true;
  return (
    String(plan.code || "")
      .trim()
      .toLowerCase() === "all"
  );
};

export const bundleCodesFromPlans = (plans = []) =>
  new Set(
    (Array.isArray(plans) ? plans : [])
      .filter(isBundlePlan)
      .map((plan) => plan.code)
      .filter(Boolean),
  );

export const isBundleCode = (code, plans = []) => {
  const value = String(code || "")
    .trim()
    .toLowerCase();
  if (!value) return false;
  if (value === "all") return true;
  const bundles = bundleCodesFromPlans(plans);
  return bundles.has(code) || bundles.has(value);
};

export const selectionHasBundle = (codes = [], plans = []) =>
  (codes || []).some((code) => isBundleCode(code, plans));

export const exclusivePlanSelection = (codes = [], plans = []) => {
  const unique = Array.from(new Set((codes || []).filter(Boolean)));
  const bundleCode = unique.find((code) => isBundleCode(code, plans));
  return bundleCode ? [bundleCode] : unique;
};

export const sortMainPlans = (plans = []) =>
  [...(Array.isArray(plans) ? plans : [])].sort(
    (left, right) => Number(isBundlePlan(left)) - Number(isBundlePlan(right)),
  );

export const splitCatalogPlans = (plans = []) => {
  const list = (Array.isArray(plans) ? plans : []).filter(
    (plan) => !isHiddenFromBillingListing(plan),
  );
  return {
    main: list.filter(isMainPlan),
    standalone: list.filter(isStandalonePlan),
  };
};

export const priceUnitLabel = (plan) =>
  isPerLocationPlan(plan) ? " / month" : " / bed / month";

export const planMonthlyTotal = (plan, beds = 0) => {
  const price = Number(plan?.price) || 0;
  if (isPerLocationPlan(plan)) return price;
  return price * Math.max(0, Number(beds) || 0);
};

export const plansMonthlyTotal = (plans = [], beds = 0) =>
  (plans || []).reduce((sum, plan) => sum + planMonthlyTotal(plan, beds), 0);

export const planCode = (plan) => {
  const code = String(plan?.code || "")
    .trim()
    .toLowerCase();
  return code || null;
};

export const looksLikeHtml = (value) =>
  /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

export const startsWithHtml = (value) => /^\s*</.test(String(value || ""));

const decodeHtmlEntities = (value) => {
  let current = String(value || "");
  let previous = "";
  while (current !== previous) {
    previous = current;
    current = current
      .replace(/&nbsp;/gi, " ")
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;|&apos;/gi, "'")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) =>
        String.fromCharCode(parseInt(code, 16)),
      )
      .replace(/&amp;/gi, "&");
  }
  return current;
};

const stripUnsafeHtml = (html) =>
  String(html || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");

export const decodeWysiwygHtml = (value) => {
  let html = String(value ?? "").trim();
  if (!html) return "";
  if (
    (html.startsWith('"') && html.endsWith('"')) ||
    (html.startsWith("'") && html.endsWith("'"))
  ) {
    try {
      const parsed = JSON.parse(html);
      if (typeof parsed === "string") html = parsed.trim();
    } catch {
      // keep the original string
    }
  }
  const hasLiveTags = looksLikeHtml(html);
  if (!hasLiveTags && /&(?:lt|gt|amp|#)/i.test(html)) {
    html = decodeHtmlEntities(html);
  } else {
    html = html.replace(/&nbsp;/gi, " ");
  }
  return stripUnsafeHtml(html).trim();
};

export const plainTextFromWysiwyg = (value, maxLength = 160) => {
  const text = decodeWysiwygHtml(value)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6]|ul|ol)>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  if (maxLength && text.length > maxLength) {
    return `${text.slice(0, maxLength).trim()}…`;
  }
  return text;
};

export const planFeatures = (plan) => {
  const raw = plan?.features;
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    const trimmed = raw.trim();
    if (
      startsWithHtml(trimmed) ||
      startsWithHtml(decodeHtmlEntities(trimmed))
    ) {
      return [trimmed];
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
      if (typeof parsed === "string" && parsed.trim()) {
        return [parsed.trim()];
      }
    } catch {
      return trimmed
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    }
  }
  return [];
};

export const planCopy = (plan) => {
  const description = decodeWysiwygHtml(plan?.description);
  const uniqueFeatures = planFeatures(plan)
    .map((item) => decodeWysiwygHtml(item))
    .filter(Boolean)
    .filter((item) => item !== description);
  const htmlChunks = [
    looksLikeHtml(description) ? description : "",
    ...uniqueFeatures.filter(looksLikeHtml),
  ].filter(Boolean);
  const textFeatures = uniqueFeatures.filter((item) => !looksLikeHtml(item));

  if (htmlChunks.length) {
    return {
      html: htmlChunks.join(""),
      description: looksLikeHtml(description) ? "" : description,
      features: textFeatures,
    };
  }

  return {
    html: "",
    description,
    features: uniqueFeatures,
  };
};

export const planPlainDescription = (plan, maxLength = 140) => {
  const copy = planCopy(plan);
  return plainTextFromWysiwyg(copy.html || copy.description, maxLength);
};

export const planLeadText = (plan, maxLength = 120) => {
  const copy = planCopy(plan);
  if (copy.html) {
    const firstParagraph = copy.html.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
    if (firstParagraph) {
      return plainTextFromWysiwyg(firstParagraph[1], maxLength);
    }
  }
  if (copy.description)
    return plainTextFromWysiwyg(copy.description, maxLength);
  if (copy.features[0])
    return plainTextFromWysiwyg(copy.features[0], maxLength);
  return "";
};

export const planHasExpandableCopy = (plan) => {
  const copy = planCopy(plan);
  const lead = planLeadText(plan, 0);
  const full = planPlainDescription(plan, 0);
  if (copy.html) {
    return (
      /<(ul|ol|li)\b/i.test(copy.html) ||
      (copy.html.match(/<p\b/gi) || []).length > 1 ||
      full.length > lead.length + 12
    );
  }
  return copy.features.length > 0 || full.length > lead.length + 12;
};

export const isLivePlan = (plan) => LIVE_PLAN_STATUSES.includes(plan?.status);

export const isManualSubscription = (plan) =>
  plan?.subcription_source_type === "admin_portal" &&
  Boolean(plan?.already_granted);

export const highlightMatchesPlan = (plan, highlightItem) => {
  const raw = String(highlightItem || "")
    .trim()
    .toLowerCase();
  if (!raw) return false;
  const key = raw.replace(/_/g, "-");
  const code = planCode(plan);
  if (code && (code === key || code === raw)) return true;
  const name = String(plan?.name || "")
    .trim()
    .toLowerCase();
  if (!name) return false;
  return (
    name === raw || name.replace(/\s+/g, "") === raw.replace(/[\s_-]/g, "")
  );
};
